import React, { useState } from 'react';
import { Home } from 'lucide-react';
import PVSPageLayout from './PVSPageLayout';

const CalculatorApp = ({ onReturnHome }) => {
  const [currentCalculator, setCurrentCalculator] = useState('menu');
  
  // Distance Calculator states
  const [vesselSize, setVesselSize] = useState('');
  const [vesselCount, setVesselCount] = useState('1');
  const [calculationResult, setCalculationResult] = useState(null);
  
  // Size Calculator states
  const [usageType, setUsageType] = useState('Domestic');
  const [appliances, setAppliances] = useState([{ category: '', totalKW: '' }]);
  const [sizeCalculationResults, setSizeCalculationResults] = useState(null);

  // Pressure Testing states
  const [testType, setTestType] = useState('');
  const [operatingPressure, setOperatingPressure] = useState('');
  const [pipeMaterial, setPipeMaterial] = useState('PE');
  const [installationCondition, setInstallationCondition] = useState('new');
  const [testMedium, setTestMedium] = useState('air_nitrogen');
  const [gaugeResolution, setGaugeResolution] = useState('0.1');
  const [pipeDetails, setPipeDetails] = useState([
    { diameter: '', length: '', fittings: '0' }
  ]);
  const [pressureTestResults, setPressureTestResults] = useState(null);
  const [pressureGuidanceError, setPressureGuidanceError] = useState('');

  // Error handling states
  const [errors, setErrors] = useState({
    vesselSize: '',
    totalKW: {}
  });

  const applianceOptions = [
    'Boiler',
    'Oven',
    'Water Heater',
    'Space Heater',
    'AGA',
    'Industrial Burner',
    'Grain Dryer',
    'Vaporiser',
    'Generator',
    'Other'
  ];

  const PIPE_VOLUME_TABLE_M3_PER_M = {
    PE: {
      20: 0.00019,
      25: 0.00033,
      32: 0.00053,
      40: 0.00084,
      63: 0.0021,
      90: 0.005,
      125: 0.01,
      180: 0.02
    },
    Copper: {
      10: 0.00006,
      15: 0.00014,
      22: 0.00032,
      28: 0.00054,
      35: 0.00084
    },
    Steel: {
      20: 0.00024,
      25: 0.00046,
      32: 0.00064,
      40: 0.0011,
      50: 0.0015,
      65: 0.0024,
      80: 0.0038,
      90: 0.0054,
      100: 0.009,
      125: 0.014,
      150: 0.02
    }
  };

  const GRM_BY_GAUGE_RESOLUTION = {
    '0.01': 0.05,
    '0.1': 0.5,
    '1.0': 5
  };

  const MAX_TTD_BY_GAUGE_RESOLUTION = {
    '0.01': 15,
    '0.1': 30,
    '1.0': 60
  };

  // CoP 22 Table 17 — equivalent pipe length per fitting (metres), by material and nominal size
  const FITTING_EQUIVALENT_LENGTH_M = {
    PE: {
      20: 0.5,
      25: 0.6,
      32: 0.8,
      40: 1.0,
      63: 1.5,
      90: 2.0,
      125: 2.5,
      180: 3.5
    },
    Copper: {
      10: 0.3,
      15: 0.4,
      22: 0.5,
      28: 0.6,
      35: 0.8
    },
    Steel: {
      20: 0.5,
      25: 0.6,
      32: 0.8,
      40: 1.0,
      50: 1.2,
      65: 1.5,
      80: 2.0,
      90: 2.2,
      100: 2.5,
      125: 3.0,
      150: 3.5
    }
  };

  const PSI_PER_BAR = 14.503773773;
  const MM_PER_INCH = 25.4;
  const LBFT_PER_NM = 0.737562149;
  const NM_PER_LBFT = 1.35581795;

  const [unitConverterTab, setUnitConverterTab] = useState('psiBar');
  const [unitInputValue, setUnitInputValue] = useState('');
  const [unitFromUnit, setUnitFromUnit] = useState('psi');

  // Validation helper function
  const validateNumber = (value, fieldName, allowZero = false) => {
    if (value === '') {
      return `${fieldName} is required`;
    }
    if (isNaN(value) || (!allowZero && value <= 0) || (allowZero && value < 0)) {
      return `${fieldName} must be a ${allowZero ? 'non-negative' : 'positive'} number`;
    }
    return '';
  };


// Calculator Functions
const WATER_TO_LPG_FILL_RATIO = 0.87;
const LPG_PROPANE_DENSITY_KG_PER_L = 0.51;
const BANKING_RULES = [
  {
    id: 'cop1_table1_small_medium',
    maxPerVesselWaterCapacityLitres: 4600,
    maxVesselsPerBank: 6,
    interBankSeparationMetres: 7.5,
    reference: 'CoP 1 Table 1 banked vessel constraint'
  }
];

const toLpgTonnes = (waterCapacityLitres) =>
  (waterCapacityLitres * WATER_TO_LPG_FILL_RATIO * LPG_PROPANE_DENSITY_KG_PER_L) / 1000;

const assessBankingLayout = (perVesselWaterCapacityLitres, vesselCount) => {
  if (vesselCount <= 1) {
    return { required: false, reviewRequired: false };
  }

  const rule = BANKING_RULES.find(
    r => perVesselWaterCapacityLitres <= r.maxPerVesselWaterCapacityLitres
  );

  if (!rule) {
    return {
      required: false,
      reviewRequired: true,
      message: 'Multiple-vessel bank spacing may apply for this capacity range. Check the relevant CoP table row for maximum vessels per bank and any inter-bank separation requirements.'
    };
  }

  if (vesselCount <= rule.maxVesselsPerBank) {
    return {
      required: false,
      reviewRequired: false,
      maxVesselsPerBank: rule.maxVesselsPerBank,
      interBankSeparationMetres: rule.interBankSeparationMetres,
      reference: rule.reference
    };
  }

  const banksNeeded = Math.ceil(vesselCount / rule.maxVesselsPerBank);
  const fullBanks = Math.floor(vesselCount / rule.maxVesselsPerBank);
  const finalBankVessels = vesselCount % rule.maxVesselsPerBank || rule.maxVesselsPerBank;

  return {
    required: true,
    reviewRequired: true,
    maxVesselsPerBank: rule.maxVesselsPerBank,
    interBankSeparationMetres: rule.interBankSeparationMetres,
    banksNeeded,
    fullBanks,
    finalBankVessels,
    interBankGaps: Math.max(0, banksNeeded - 1),
    reference: rule.reference,
    message: `Layout should be split into ${banksNeeded} banks (max ${rule.maxVesselsPerBank} vessels per bank) with ${rule.interBankSeparationMetres}m separation between banks.`
  };
};

const calculateDistances = (waterCapacityLitres, vesselCount) => {
  const perVesselWaterCapacityLitres = waterCapacityLitres;
  const totalWaterCapacityLitres = perVesselWaterCapacityLitres * vesselCount;
  const perVesselLpgTonnes = toLpgTonnes(perVesselWaterCapacityLitres);
  const totalLpgTonnes = perVesselLpgTonnes * vesselCount;
  const banking = assessBankingLayout(perVesselWaterCapacityLitres, vesselCount);
  let withoutFirewall, withFirewall, betweenVessels, note;

  if (vesselCount === 1) {
    if (totalWaterCapacityLitres <= 500) {
      withoutFirewall = '2.5';
      withFirewall = '0.3';
      betweenVessels = 'Not applicable (single vessel)';
    } else if (totalWaterCapacityLitres <= 2500) {
      withoutFirewall = '3.0';
      withFirewall = '1.5';
      betweenVessels = 'Not applicable (single vessel)';
    } else if (totalWaterCapacityLitres <= 9000) {
      withoutFirewall = '7.5';
      withFirewall = '4.0';
      betweenVessels = 'Not applicable (single vessel)';
    } else if (totalWaterCapacityLitres <= 135000) {
      withoutFirewall = '15.0';
      withFirewall = '7.5';
      betweenVessels = 'Not applicable (single vessel)';
    } else if (totalWaterCapacityLitres <= 337500) {
      withoutFirewall = '22.5';
      withFirewall = '11.0';
      betweenVessels = 'Not applicable (single vessel)';
      note = 'Special considerations apply for vessels of this size. Consult relevant codes of practice.';
    } else {
      withoutFirewall = '30.0 (See Codes of Practice)';
      withFirewall = '15.0 (See Codes of Practice)';
      betweenVessels = 'Not applicable (single vessel)';
      note = 'Volume requires special consideration. Consult relevant codes of practice for detailed requirements.';
    }
  } else {
    if (totalWaterCapacityLitres <= 1600) {
      withoutFirewall = '2.5';
      withFirewall = '0.3';
      betweenVessels = '1.0';
    } else if (totalWaterCapacityLitres <= 7000) {
      withoutFirewall = '3.0';
      withFirewall = '1.5';
      betweenVessels = '1.0';
    } else if (totalWaterCapacityLitres <= 25000) {
      withoutFirewall = '7.5';
      withFirewall = '4.0';
      betweenVessels = '1.0';
    } else if (totalWaterCapacityLitres <= 400000) {
      withoutFirewall = '15.0';
      withFirewall = '7.5';
      betweenVessels = '1.5';
    } else if (totalWaterCapacityLitres <= 920000) {
      withoutFirewall = '22.5';
      withFirewall = '11.0';
      betweenVessels = '1/4 of sum of the diameter of 2 adjacent vessels';
      note = 'For vessels of this size, additional safety measures may be required. Consult relevant codes of practice.';
    } else if (totalWaterCapacityLitres <= 2000000) {
      withoutFirewall = '30.0 (See Codes of Practice)';
      withFirewall = '15.0 (See Codes of Practice)';
      betweenVessels = '1/4 of sum of the diameter of 2 adjacent vessels';
      note = 'Installation requires specialized design considerations. Consult relevant codes of practice for detailed requirements.';
    } else {
      withoutFirewall = 'Volume exceeds maximum limit';
      withFirewall = 'Volume exceeds maximum limit';
      betweenVessels = 'Volume exceeds maximum limit';
      note = 'Total volume exceeds maximum allowable limit. Installation not permitted without special authorization.';
    }
  }

  return {
    withoutFirewall,
    withFirewall,
    betweenVessels,
    totalWaterCapacityLitres,
    perVesselWaterCapacityLitres,
    totalLpgTonnes,
    perVesselLpgTonnes,
    isMultipleVessels: vesselCount > 1,
    banking,
    note
  };
};

const handleCalculate = () => {
  const distances = calculateDistances(Number(vesselSize), Number(vesselCount));
  setCalculationResult({
    ...distances,
    vesselSize: Number(vesselSize),
    vesselCount: Number(vesselCount)
  });
};

const calculateUsage = () => {
  const validAppliances = appliances.filter(a => a.category && a.totalKW && Number(a.totalKW) > 0);
  const totalKW = validAppliances.reduce((sum, a) => sum + Number(a.totalKW), 0);

  setSizeCalculationResults({
    appliances: validAppliances,
    totals: { totalKW },
    regulatoryNotes: [
      'CoP 22:2025 Section 4.1 addresses LPG pipe sizing; connected load is one input but final sizing is pressure/flow-path dependent.',
      'Storage vessel selection must be verified against CoP 1 (relevant part) and site-specific demand profile, not generic appliance-hour assumptions.',
      'This tool is a connected-load summary to support engineering review and verified CoP lookup.'
    ]
  });
};

const calculateInternalVolume = () => {
  let total = 0;
  for (const p of pipeDetails) {
    const diameterKey = Number(p.diameter || 0);
    const perM = PIPE_VOLUME_TABLE_M3_PER_M[pipeMaterial]?.[diameterKey];
    const fittingLength = FITTING_EQUIVALENT_LENGTH_M[pipeMaterial]?.[diameterKey];
    if (!perM || fittingLength === undefined) {
      return {
        ok: false,
        error: `Nominal size ${p.diameter} mm is not in the CoP 22 Table 16/17 map for ${pipeMaterial}.`
      };
    }
    const pipeLengthM = Number(p.length || 0);
    const fittingCount = Number(p.fittings || 0);
    total += perM * (pipeLengthM + fittingLength * fittingCount);
  }
  return { ok: true, volumeM3: total };
};

const formatConversionValue = (value) => {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs === 0) return '0';
  if (abs >= 1000) return value.toFixed(2);
  if (abs >= 1) return value.toFixed(4);
  return value.toFixed(6);
};

const convertUnitValue = (value, type, fromUnit) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;

  switch (type) {
    case 'psiBar':
      return fromUnit === 'psi'
        ? { primary: num, secondary: num / PSI_PER_BAR, primaryUnit: 'psi', secondaryUnit: 'bar' }
        : { primary: num, secondary: num * PSI_PER_BAR, primaryUnit: 'bar', secondaryUnit: 'psi' };
    case 'inchMm':
      return fromUnit === 'inch'
        ? { primary: num, secondary: num * MM_PER_INCH, primaryUnit: 'inch', secondaryUnit: 'mm' }
        : { primary: num, secondary: num / MM_PER_INCH, primaryUnit: 'mm', secondaryUnit: 'inch' };
    case 'nmLbft':
      return fromUnit === 'nm'
        ? { primary: num, secondary: num * LBFT_PER_NM, primaryUnit: 'N·m', secondaryUnit: 'lb·ft' }
        : { primary: num, secondary: num * NM_PER_LBFT, primaryUnit: 'lb·ft', secondaryUnit: 'N·m' };
    default:
      return null;
  }
};

const calculateTestRequirements = () => {
  setPressureGuidanceError('');
  const opBar = Number(operatingPressure);
  const opMbar = opBar * 1000;
  const ivResult = calculateInternalVolume();
  if (!ivResult.ok && testType !== 'Let By Test') {
    setPressureGuidanceError(ivResult.error);
    setPressureTestResults(null);
    return;
  }

  if (testType === 'Let By Test') {
    const letByPressure = opMbar <= 75
      ? 'Pressure downstream between 7 and 10 mbar'
      : 'Approximately 50% of nominal operating pressure';
    setPressureTestResults({
      method: 'CoP 22:2025 Table 17/18 (Test Type A - Let by test)',
      testPressure: letByPressure,
      stabilisationPeriod: 'Per competent-person method statement',
      testDuration: 'Per competent-person method statement',
      internalVolumeM3: ivResult.ok ? ivResult.volumeM3 : null
    });
    return;
  }

  if (testType === 'Strength Test') {
    if (pipeMaterial === 'PE' && opBar > 2) {
      setPressureGuidanceError('For PE, CoP 22 Table 12 limits MOP to 2 bar for this strength-test method.');
      setPressureTestResults(null);
      return;
    }

    let stp;
    let stabilisation;
    let duration;

    if (opBar <= 0.1) {
      stp = 0.35;
      stabilisation = 5;
      duration = 5;
    } else if (opBar <= 2) {
      stp = 3;
      stabilisation = pipeMaterial === 'PE' ? 30 : 10;
      duration = pipeMaterial === 'PE' ? 15 : 5;
    } else if (opBar <= 16) {
      stp = opBar * 1.5;
      stabilisation = 15;
      duration = 30;
    } else {
      setPressureGuidanceError('Operating pressure exceeds the implemented CoP 22 Table 12 range.');
      setPressureTestResults(null);
      return;
    }

    setPressureTestResults({
      method: 'CoP 22:2025 Table 12 (Strength test duration)',
      testPressure: `${stp.toFixed(2)} bar`,
      stabilisationPeriod: `${stabilisation} minutes`,
      testDuration: `${duration} minutes`,
      internalVolumeM3: ivResult.ok ? ivResult.volumeM3 : null
    });
    return;
  }

  if (testType === 'Tightness Test') {
    const grm = GRM_BY_GAUGE_RESOLUTION[gaugeResolution];
    const maxTtd = MAX_TTD_BY_GAUGE_RESOLUTION[gaugeResolution];
    const iv = ivResult.volumeM3;
    let ttdFactor;
    let preferredTestPressure;

    if (installationCondition === 'new') {
      if (testMedium === 'butane') {
        setPressureGuidanceError('CoP 22 Table 15: new butane tightness method is not permitted in this calculator path.');
        setPressureTestResults(null);
        return;
      }
      ttdFactor = 224;

      if (opMbar <= 37) {
        preferredTestPressure = testMedium === 'propane' ? '30 to 31 mbar' : '350 mbar';
      } else if (opMbar <= 75) {
        preferredTestPressure = testMedium === 'propane' ? '60 mbar' : '350 mbar';
      } else {
        preferredTestPressure = testMedium === 'air_nitrogen' ? '3 bar' : 'Not permitted for this path';
      }
    } else {
      if (testMedium === 'propane') ttdFactor = 4.8;
      if (testMedium === 'air_nitrogen') ttdFactor = 10.4;
      if (testMedium === 'butane') ttdFactor = 6.0;

      if (opMbar <= 37) {
        preferredTestPressure = testMedium === 'butane' ? '20 to 21 mbar' : (testMedium === 'propane' ? '30 to 31 mbar' : 'Refer method statement');
      } else if (opMbar <= 75) {
        preferredTestPressure = testMedium === 'propane' ? '60 mbar' : 'N/A for selected test medium';
      } else {
        preferredTestPressure = `${(opBar * 0.8).toFixed(2)} bar (80% of nominal operating pressure)`;
      }
    }

    if (!ttdFactor) {
      setPressureGuidanceError('Unable to determine TTD factor for this condition/test-medium combination.');
      setPressureTestResults(null);
      return;
    }

    const rawTtd = ttdFactor * grm * iv;
    const roundedTtd = Math.max(2, Math.ceil(rawTtd));
    const cappedTtd = Math.min(roundedTtd, maxTtd);
    const stabilisation = iv > 0.035 ? Math.max(15, cappedTtd) : cappedTtd;

    setPressureTestResults({
      method: 'CoP 22:2025 Tables 14, 15, 16, 17/18 (tightness testing path)',
      testPressure: preferredTestPressure,
      stabilisationPeriod: `${stabilisation} minutes`,
      testDuration: `${cappedTtd} minutes`,
      grm,
      internalVolumeM3: iv,
      notes: [
        'TTD rounded up to next full minute, minimum 2 minutes.',
        `Gauge resolution ${gaugeResolution} mbar gives GRM ${grm} mbar and max TTD ${maxTtd} minutes.`,
        'For service pipework volume > 0.035 m³, stabilisation time set at least equal to TTD with a 15-minute minimum.',
        'Internal volume includes pipe length plus CoP 22 Table 17 equivalent length per fitting.'
      ]
    });
    return;
  }

  setPressureGuidanceError('Select a valid test type.');
  setPressureTestResults(null);
};
// Handler Functions
const handleRemoveAppliance = (index) => {
  const newAppliances = appliances.filter((_, i) => i !== index);
  setAppliances(newAppliances);
  const newErrors = { ...errors };
  delete newErrors.totalKW[index];
  setErrors(newErrors);
};

const handleAddAppliance = () => {
  setAppliances([...appliances, { category: '', totalKW: '' }]);
};

const handleApplianceChange = (index, field, value) => {
  const newAppliances = [...appliances];
  newAppliances[index] = { ...newAppliances[index], [field]: value };
  setAppliances(newAppliances);

  if (field === 'totalKW') {
    setErrors(prev => ({
      ...prev,
      totalKW: {
        ...prev.totalKW,
        [index]: validateNumber(value, 'Total KW')
      }
    }));
  }
};

const handlePipeDetailChange = (index, field, value) => {
  const newDetails = [...pipeDetails];
  newDetails[index] = { ...newDetails[index], [field]: value };
  setPipeDetails(newDetails);
};

const handleAddPipe = () => {
  setPipeDetails([...pipeDetails, { diameter: '', length: '', fittings: '0' }]);
};

const handleRemovePipe = (index) => {
  const newDetails = pipeDetails.filter((_, i) => i !== index);
  setPipeDetails(newDetails);
};

// Render Functions
const renderCalculatorMenu = () => (
  <PVSPageLayout className="p-8">
    <div className="max-w-4xl mx-auto">
      <div className="pvs-glass p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-pvs-accent">LPG Calculators</h2>
          <button
            onClick={onReturnHome}
            className="group flex items-center justify-center w-12 h-12 pvs-glass pvs-glass-hover"
            title="Return to Homepage"
          >
            <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <button
            onClick={() => setCurrentCalculator('distance')}
            className="p-6 pvs-glass pvs-glass-hover"
          >
            <h2 className="text-xl font-semibold text-white mb-2">Distance Calculator</h2>
            <p className="text-pvs-text-muted">Calculate distances based on vessel size and count</p>
          </button>
          
          <button
            onClick={() => setCurrentCalculator('size')}
            className="p-6 pvs-glass pvs-glass-hover"
          >
            <h2 className="text-xl font-semibold text-white mb-2">Size and Usage Calculator</h2>
            <p className="text-pvs-text-muted">Compute connected load and map required CoP verification steps</p>
          </button>

          <button
            onClick={() => setCurrentCalculator('pressure')}
            className="p-6 pvs-glass pvs-glass-hover"
          >
            <h2 className="text-xl font-semibold text-white mb-2">Pressure Testing</h2>
            <p className="text-pvs-text-muted">Apply CoP 22:2025 pressure-testing tables and constraints</p>
          </button>

          <button
            onClick={() => setCurrentCalculator('units')}
            className="p-6 pvs-glass pvs-glass-hover"
          >
            <h2 className="text-xl font-semibold text-white mb-2">Unit Converters</h2>
            <p className="text-pvs-text-muted">Convert PSI/bar, inch/mm, and N·m/lb·ft</p>
          </button>
        </div>
      </div>
    </div>
  </PVSPageLayout>
);

const renderDistanceCalculator = () => (
  <PVSPageLayout className="p-8">
    <div className="max-w-2xl mx-auto">
      <div className="pvs-glass p-6">
        <h2 className="text-2xl font-bold text-pvs-accent mb-6">Distance Calculator</h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-pvs-text-muted mb-2">
              Size of vessel (water capacity, litres)
            </label>
            <input
              type="number"
              value={vesselSize}
              onChange={(e) => {
                const value = e.target.value;
                setVesselSize(value);
                setErrors(prev => ({
                  ...prev,
                  vesselSize: validateNumber(value, 'Vessel size')
                }));
              }}
              min="1"
              step="1"
              className={`pvs-input ${
                errors.vesselSize ? 'border-red-500' : ''
              }`}
              placeholder="e.g. 2500"
            />
            {errors.vesselSize && (
              <p className="mt-1 text-sm text-red-400">{errors.vesselSize}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-pvs-text-muted mb-2">
              Number of vessels
            </label>
            <select
              value={vesselCount}
              onChange={(e) => setVesselCount(e.target.value)}
              className="pvs-select"
            >
              {[...Array(20)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>

          {calculationResult && (
            <div className="mt-6 p-4 bg-pvs-card-light rounded-lg border border-pvs-border">
              <h3 className="text-lg font-semibold text-pvs-accent mb-4">Calculation Results:</h3>
              
              <div className="mb-4 p-3 bg-pvs-card border border-pvs-border rounded-md">
                <h4 className="font-medium text-pvs-accent">Configuration:</h4>
                <p className="text-pvs-text-muted">
                  {calculationResult.isMultipleVessels ? 
                    `Multiple Vessel Installation (${calculationResult.vesselCount ?? 0} vessels)` : 
                    'Single Vessel Installation'}
                </p>
                <p className="text-pvs-text-muted">
                  <span className="font-medium">Individual Vessel Water Capacity:</span>{' '}
                  {calculationResult.perVesselWaterCapacityLitres ? Math.round(calculationResult.perVesselWaterCapacityLitres).toLocaleString() : '0'} litres
                </p>
                <p className="text-pvs-text-muted">
                  <span className="font-medium">Equivalent Individual LPG Capacity (approx.):</span>{' '}
                  {calculationResult.perVesselLpgTonnes ? calculationResult.perVesselLpgTonnes.toFixed(2) : '0.00'} tonnes
                </p>
                <p className="text-pvs-text-muted">
                  <span className="font-medium">Total Water Capacity:</span>{' '}
                  {calculationResult.totalWaterCapacityLitres ? Math.round(calculationResult.totalWaterCapacityLitres).toLocaleString() : '0'} litres
                </p>
                <p className="text-pvs-text-muted">
                  <span className="font-medium">Equivalent Total LPG Capacity (approx.):</span>{' '}
                  {calculationResult.totalLpgTonnes ? calculationResult.totalLpgTonnes.toFixed(2) : '0.00'} tonnes
                </p>
              </div>

              <div className="space-y-4 text-pvs-text-muted">
                <div className="p-3 bg-pvs-card border border-pvs-border rounded-md">
                  <p className="text-pvs-accent font-medium">
                    Distance without fire wall:
                  </p>
                  <p className="text-lg font-bold text-pvs-accent ml-4">
                    {calculationResult.withoutFirewall} metres
                  </p>
                </div>

                <div className="p-3 bg-pvs-card border border-pvs-border rounded-md">
                  <p className="text-pvs-accent font-medium">
                    Distance with fire wall:
                  </p>
                  <p className="text-lg font-bold text-pvs-accent ml-4">
                    {calculationResult.withFirewall} metres
                  </p>
                </div>

                <div className="p-3 bg-pvs-card border border-pvs-border rounded-md">
                  <p className="text-pvs-accent font-medium">
                    Distances between vessels:
                  </p>
                  <p className="text-lg font-bold text-pvs-accent ml-4">
                    {calculationResult.betweenVessels}
                    {['1.0', '1.5'].includes(calculationResult.betweenVessels) ? ' metres' : ''}
                  </p>
                </div>
              </div>

              {calculationResult.note && (
                <div className="mt-4 p-3 bg-pvs-accent/5 text-pvs-accent rounded-md border border-pvs-accent/20">
                  <p className="font-medium">📋 Important Note:</p>
                  <p>{calculationResult.note}</p>
                </div>
              )}
              {calculationResult.banking?.required && (
                <div className="mt-4 p-3 bg-amber-900/20 text-amber-400 rounded-md border border-amber-500/30">
                  <p className="font-medium">⚠️ Bank Separation Required</p>
                  <p>{calculationResult.banking.message}</p>
                  <p className="mt-1">
                    Bank layout: {calculationResult.banking.fullBanks} full bank(s)
                    {calculationResult.banking.finalBankVessels !== calculationResult.banking.maxVesselsPerBank
                      ? ` + 1 partial bank (${calculationResult.banking.finalBankVessels} vessel${calculationResult.banking.finalBankVessels > 1 ? 's' : ''})`
                      : ''}
                    . Inter-bank gaps required: {calculationResult.banking.interBankGaps}.
                  </p>
                  <p className="mt-1 text-sm">
                    Reference: {calculationResult.banking.reference}
                  </p>
                </div>
              )}
              {!calculationResult.banking?.required && calculationResult.banking?.reviewRequired && (
                <div className="mt-4 p-3 bg-amber-900/20 text-amber-400 rounded-md border border-amber-500/30">
                  <p className="font-medium">⚠️ Additional Layout Review Needed</p>
                  <p>{calculationResult.banking.message}</p>
                </div>
              )}
              <div className="mt-3 p-3 bg-pvs-accent/5 text-pvs-accent rounded-md border border-pvs-accent/20">
                <p className="font-medium">Conversion basis used</p>
                <p>
                  LPG tonnes shown are estimated from water capacity using an 87% fill limit and propane density of 0.51 kg/L.
                  Always verify against the exact CoP 1 Table 1 row for your installation details.
                </p>
              </div>
            </div>
          )}

          <div className="flex space-x-4">
            <button
              onClick={() => setCurrentCalculator('menu')}
              className="px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10"
            >
              Return to Calculators
            </button>

            <button
              onClick={onReturnHome}
              className="group flex items-center justify-center w-12 h-12 pvs-glass pvs-glass-hover"
              title="Return to Homepage"
            >
              <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>

            <button
              onClick={handleCalculate}
              disabled={!!errors.vesselSize || !vesselSize}
              className={`flex-1 px-4 py-2 rounded-md shadow-sm text-sm font-medium ${
                errors.vesselSize || !vesselSize
                  ? 'bg-gray-700 cursor-not-allowed text-gray-500'
                  : 'pvs-btn-accent text-white'
              }`}
            >
              Calculate
            </button>
          </div>
        </div>
      </div>
    </div>
  </PVSPageLayout>
);
// Size Calculator render function
const renderSizeCalculator = () => (
  <PVSPageLayout className="p-8">
    <div className="max-w-2xl mx-auto">
      <div className="pvs-glass p-6">
        <h2 className="text-2xl font-bold text-pvs-accent mb-6">Size and Usage Calculator</h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-pvs-text-muted mb-2">
              Usage type
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => setUsageType('Domestic')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  usageType === 'Domestic'
                    ? 'pvs-btn-accent text-white'
                    : 'bg-pvs-card border border-pvs-border text-pvs-text-muted hover:bg-pvs-accent/10'
                }`}
              >
                Domestic
              </button>
              <button
                onClick={() => setUsageType('Industrial')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  usageType === 'Industrial'
                    ? 'pvs-btn-accent text-white'
                    : 'bg-pvs-card border border-pvs-border text-pvs-text-muted hover:bg-pvs-accent/10'
                }`}
              >
                Industrial
              </button>
            </div>
          </div>

          {appliances.map((appliance, index) => (
            <div key={index} className="space-y-4 bg-pvs-card p-4 rounded-lg border border-pvs-border">
              <div className="flex justify-between items-start">
                <div className="flex-grow space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                      Appliance category
                    </label>
                    <select
                      value={appliance.category}
                      onChange={(e) => handleApplianceChange(index, 'category', e.target.value)}
                      className="pvs-select"
                    >
                      <option value="">Select category</option>
                      {applianceOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                      Total KW
                    </label>
                    <input
                      type="number"
                      value={appliance.totalKW}
                      onChange={(e) => handleApplianceChange(index, 'totalKW', e.target.value)}
                      min="0"
                      step="1"
                      className={`pvs-input ${
                        errors.totalKW[index] ? 'border-red-500' : ''
                      }`}
                      placeholder="Enter KW"
                    />
                    {errors.totalKW[index] && (
                      <p className="mt-1 text-sm text-red-400">{errors.totalKW[index]}</p>
                    )}
                  </div>
                </div>
                
                {appliances.length > 1 && (
                  <button
                    onClick={() => handleRemoveAppliance(index)}
                    className="ml-4 p-1 text-gray-400 hover:text-red-500"
                    type="button"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}

          {sizeCalculationResults && (
            <div className="mt-6 p-4 bg-pvs-card-light rounded-lg border border-pvs-border">
              <h3 className="text-lg font-semibold text-pvs-accent mb-4">Sizing Summary:</h3>
              
              <div className="mb-4 p-3 bg-pvs-card border border-pvs-border rounded-md">
                <h4 className="font-medium text-pvs-accent">Total Requirements:</h4>
                <p className="text-pvs-text-muted">
                  <span className="font-medium">Total Connected Load:</span>{' '}
                  {sizeCalculationResults.totals.totalKW.toFixed(2)} kW
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-medium text-pvs-accent">Breakdown by Appliance:</h4>
                {sizeCalculationResults.appliances.map((appliance, index) => (
                  <div key={index} className="p-3 bg-pvs-card border border-pvs-border rounded-md">
                    <p className="font-medium text-pvs-accent">{appliance.category}</p>
                    <div className="ml-4 space-y-1 text-sm">
                      <p>Capacity: {appliance.totalKW} kW</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-pvs-card border border-pvs-border rounded-md">
                <p className="font-medium text-pvs-accent mb-2">Required CoP Verification</p>
                <ul className="list-disc ml-5 text-pvs-text-muted space-y-1">
                  {sizeCalculationResults.regulatoryNotes.map((note, idx) => (
                    <li key={idx}>{note}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 p-3 bg-pvs-accent/5 text-pvs-accent rounded-md border border-pvs-accent/20">
                <p className="font-medium">Regulatory integrity note</p>
                <p>
                  Connected-load maths is shown for planning only. Regulatory sizing decisions are taken from verified CoP guidance above, not from hardcoded assumptions.
                </p>
              </div>
            </div>
          )}

          <button
            onClick={handleAddAppliance}
            className="w-full px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10"
          >
            Add Another Appliance
          </button>

          <div className="flex space-x-4">
            <button
              onClick={() => setCurrentCalculator('menu')}
              className="px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10"
            >
              Return to Calculators
            </button>

            <button
              onClick={onReturnHome}
              className="group flex items-center justify-center w-12 h-12 pvs-glass pvs-glass-hover"
              title="Return to Homepage"
            >
              <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
            </button>

            <button
              onClick={calculateUsage}
              disabled={Object.values(errors.totalKW).some(error => error) || 
                       appliances.some(app => !app.category || !app.totalKW)}
              className={`flex-1 px-4 py-2 rounded-md shadow-sm text-sm font-medium ${
                Object.values(errors.totalKW).some(error => error) || 
                appliances.some(app => !app.category || !app.totalKW)
                  ? 'bg-gray-700 cursor-not-allowed text-gray-500'
                  : 'pvs-btn-accent text-white'
              }`}
            >
              Calculate
            </button>
          </div>
        </div>
      </div>
    </div>
  </PVSPageLayout>
  );

  const renderPressureTesting = () => (
    <PVSPageLayout className="p-8">
      <div className="max-w-2xl mx-auto">
        <div className="pvs-glass p-6">
          <h2 className="text-2xl font-bold text-pvs-accent mb-6">Pressure Testing Calculator</h2>

          <div className="mb-6">
            <div className="bg-amber-900/20 border-l-4 border-amber-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-400">
                    This calculator is for guidance only and should be used by qualified professionals 
                    who must always refer to relevant codes of practice.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Test Type Selection */}
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                Type of Test
              </label>
              <select
                value={testType}
                onChange={(e) => setTestType(e.target.value)}
                className="pvs-select"
              >
                <option value="">Select test type</option>
                <option value="Let By Test">Let By Test</option>
                <option value="Tightness Test">Tightness Test</option>
                <option value="Strength Test">Strength Test</option>
              </select>
            </div>

            {/* Operating Pressure Input */}
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                Operating Pressure (BAR)
              </label>
              <input
                type="number"
                min="0.01"
                step="0.1"
                value={operatingPressure}
                onChange={(e) => setOperatingPressure(e.target.value)}
                className="pvs-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Pipe Material
                </label>
                <select
                  value={pipeMaterial}
                  onChange={(e) => setPipeMaterial(e.target.value)}
                  className="pvs-select"
                >
                  <option value="PE">PE</option>
                  <option value="Copper">Copper</option>
                  <option value="Steel">Steel</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Pipework Condition
                </label>
                <select
                  value={installationCondition}
                  onChange={(e) => setInstallationCondition(e.target.value)}
                  className="pvs-select"
                >
                  <option value="new">New</option>
                  <option value="existing">Existing</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Test Medium
                </label>
                <select
                  value={testMedium}
                  onChange={(e) => setTestMedium(e.target.value)}
                  className="pvs-select"
                >
                  <option value="air_nitrogen">Air / Nitrogen</option>
                  <option value="propane">Propane</option>
                  <option value="butane">Butane</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                  Gauge Resolution (mbar)
                </label>
                <select
                  value={gaugeResolution}
                  onChange={(e) => setGaugeResolution(e.target.value)}
                  className="pvs-select"
                >
                  <option value="0.1">0.1</option>
                  <option value="0.01">0.01</option>
                  <option value="1.0">1.0</option>
                </select>
              </div>
            </div>

            {/* Pipe Details */}
            {pipeDetails.map((pipe, index) => (
              <div key={index} className="space-y-4 bg-pvs-card p-4 rounded-lg border border-pvs-border">
                <div className="flex justify-between items-start">
                  <div className="flex-grow space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                          Diameter of Pipe (mm)
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={pipe.diameter}
                          onChange={(e) => handlePipeDetailChange(index, 'diameter', e.target.value)}
                          className="pvs-input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                          Length of Pipe (meters)
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={pipe.length}
                          onChange={(e) => handlePipeDetailChange(index, 'length', e.target.value)}
                          className="pvs-input"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                          Quantity of Fittings
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={pipe.fittings}
                          onChange={(e) => handlePipeDetailChange(index, 'fittings', e.target.value)}
                          className="pvs-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {pipeDetails.length > 1 && (
                    <button
                      onClick={() => handleRemovePipe(index)}
                      className="ml-4 p-1 text-gray-400 hover:text-red-500"
                      type="button"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}

            <button
              onClick={handleAddPipe}
              className="w-full px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10"
            >
              Add Another Size of Pipe
            </button>

            <div className="flex space-x-4">
              <button
                onClick={() => setCurrentCalculator('menu')}
                className="px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10"
              >
                Return to Calculators
              </button>

              <button
                onClick={onReturnHome}
                className="group flex items-center justify-center w-12 h-12 pvs-glass pvs-glass-hover"
                title="Return to Homepage"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>

              <button
                onClick={calculateTestRequirements}
                disabled={
                  !testType ||
                  !operatingPressure ||
                  (testType !== 'Let By Test' &&
                    pipeDetails.some((pipe) => !pipe.diameter || !pipe.length))
                }
                className={`flex-1 px-4 py-2 rounded-md shadow-sm text-sm font-medium ${
                !testType ||
                !operatingPressure ||
                (testType !== 'Let By Test' &&
                  pipeDetails.some((pipe) => !pipe.diameter || !pipe.length))
                    ? 'bg-gray-700 cursor-not-allowed text-gray-500'
                    : 'pvs-btn-accent text-white'
                }`}
              >
                Calculate
              </button>
            </div>

          {pressureGuidanceError && (
            <div className="p-3 bg-red-900/30 text-red-400 rounded-md border border-red-500/30">
              {pressureGuidanceError}
            </div>
          )}

            {pressureTestResults && (
              <div className="mt-6 p-4 bg-pvs-card-light rounded-lg border border-pvs-border">
                <h3 className="text-lg font-semibold text-pvs-accent mb-4">Pressure-Test Result (CoP 22:2025):</h3>

                <div className="p-3 bg-pvs-card border border-pvs-border rounded-md space-y-2 text-pvs-text-muted">
                  <p><span className="font-medium">Method reference:</span> {pressureTestResults.method}</p>
                  <p><span className="font-medium">Preferred test pressure:</span> {pressureTestResults.testPressure}</p>
                  <p><span className="font-medium">Stabilisation period:</span> {pressureTestResults.stabilisationPeriod}</p>
                  <p><span className="font-medium">Test duration:</span> {pressureTestResults.testDuration}</p>
                  {typeof pressureTestResults.grm !== 'undefined' && (
                    <p><span className="font-medium">Gauge readable movement (GRM):</span> {pressureTestResults.grm} mbar</p>
                  )}
                  {typeof pressureTestResults.internalVolumeM3 === 'number' && (
                    <p><span className="font-medium">Calculated internal volume (IV):</span> {pressureTestResults.internalVolumeM3.toFixed(4)} m³</p>
                  )}
                  {pressureTestResults.notes?.length > 0 && (
                    <ul className="list-disc ml-5 space-y-1">
                      {pressureTestResults.notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PVSPageLayout>
  );

  const UNIT_CONVERTER_TABS = [
    { id: 'psiBar', label: 'PSI ↔ Bar', fromUnit: 'psi', fromLabel: 'PSI', toLabel: 'Bar' },
    { id: 'inchMm', label: 'Inch ↔ mm', fromUnit: 'inch', fromLabel: 'Inch', toLabel: 'mm' },
    { id: 'nmLbft', label: 'N·m ↔ lb·ft', fromUnit: 'nm', fromLabel: 'N·m', toLabel: 'lb·ft' }
  ];

  const activeUnitTab = UNIT_CONVERTER_TABS.find((tab) => tab.id === unitConverterTab) || UNIT_CONVERTER_TABS[0];
  const unitConversionResult = convertUnitValue(unitInputValue, unitConverterTab, unitFromUnit);

  const handleUnitTabChange = (tabId) => {
    const tab = UNIT_CONVERTER_TABS.find((t) => t.id === tabId);
    setUnitConverterTab(tabId);
    setUnitFromUnit(tab?.fromUnit || 'psi');
    setUnitInputValue('');
  };

  const renderUnitConverters = () => (
    <PVSPageLayout className="p-8">
      <div className="max-w-2xl mx-auto">
        <div className="pvs-glass p-6">
          <h2 className="text-2xl font-bold text-pvs-accent mb-6">Unit Converters</h2>

          <div className="flex flex-wrap gap-2 mb-6">
            {UNIT_CONVERTER_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleUnitTabChange(tab.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  unitConverterTab === tab.id
                    ? 'pvs-btn-accent text-white'
                    : 'bg-pvs-card border border-pvs-border text-pvs-text-muted hover:bg-pvs-accent/10'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                Convert from
              </label>
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setUnitFromUnit(activeUnitTab.fromUnit)}
                  className={`px-4 py-2 rounded-md text-sm ${
                    unitFromUnit === activeUnitTab.fromUnit
                      ? 'pvs-btn-accent text-white'
                      : 'bg-pvs-card border border-pvs-border text-pvs-text-muted'
                  }`}
                >
                  {activeUnitTab.fromLabel}
                </button>
                <button
                  onClick={() =>
                    setUnitFromUnit(
                      unitConverterTab === 'psiBar'
                        ? 'bar'
                        : unitConverterTab === 'inchMm'
                          ? 'mm'
                          : 'lbft'
                    )
                  }
                  className={`px-4 py-2 rounded-md text-sm ${
                    unitFromUnit !== activeUnitTab.fromUnit
                      ? 'pvs-btn-accent text-white'
                      : 'bg-pvs-card border border-pvs-border text-pvs-text-muted'
                  }`}
                >
                  {activeUnitTab.toLabel}
                </button>
              </div>

              <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                Value ({unitFromUnit === activeUnitTab.fromUnit ? activeUnitTab.fromLabel : activeUnitTab.toLabel})
              </label>
              <input
                type="number"
                step="any"
                value={unitInputValue}
                onChange={(e) => setUnitInputValue(e.target.value)}
                className="pvs-input"
                placeholder="Enter value"
              />
            </div>

            {unitConversionResult && (
              <div className="p-4 bg-pvs-card-light rounded-lg border border-pvs-border">
                <h3 className="text-lg font-semibold text-pvs-accent mb-4">Result</h3>
                <div className="space-y-2 text-pvs-text-muted">
                  <p>
                    <span className="font-medium text-pvs-accent">
                      {unitConversionResult.primaryUnit}:
                    </span>{' '}
                    {formatConversionValue(unitConversionResult.primary)}
                  </p>
                  <p>
                    <span className="font-medium text-pvs-accent">
                      {unitConversionResult.secondaryUnit}:
                    </span>{' '}
                    {formatConversionValue(unitConversionResult.secondary)}
                  </p>
                </div>
              </div>
            )}

            <div className="p-3 bg-pvs-accent/5 text-pvs-accent rounded-md border border-pvs-accent/20 text-sm">
              <p className="font-medium mb-1">Conversion factors used</p>
              {unitConverterTab === 'psiBar' && (
                <p>1 bar = {PSI_PER_BAR} psi (exact definition: 1 bar = 100 kPa)</p>
              )}
              {unitConverterTab === 'inchMm' && (
                <p>1 inch = {MM_PER_INCH} mm (international inch definition)</p>
              )}
              {unitConverterTab === 'nmLbft' && (
                <p>1 N·m = {LBFT_PER_NM} lb·ft · 1 lb·ft = {NM_PER_LBFT} N·m</p>
              )}
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setCurrentCalculator('menu')}
                className="px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10"
              >
                Return to Calculators
              </button>

              <button
                onClick={onReturnHome}
                className="group flex items-center justify-center w-12 h-12 pvs-glass pvs-glass-hover"
                title="Return to Homepage"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </PVSPageLayout>
  );

// Main component render switch
switch (currentCalculator) {
  case 'distance':
    return renderDistanceCalculator();
  case 'size':
    return renderSizeCalculator();
  case 'pressure':
    return renderPressureTesting();
  case 'units':
    return renderUnitConverters();
  default:
    return renderCalculatorMenu();
}
};

export default CalculatorApp;